import { useState } from "react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  HandPlatter, ChefHat, ReceiptEuro, BarChart3, QrCode, Bell,
  Download, Lightbulb, CheckCircle2, LifeBuoy, Loader2,
} from "lucide-react";

// Single source of truth: used for both on-screen cards and the PDF export.
const GUIDE = [
  {
    key: "pos",
    title: "Salle / POS — Prise de commande",
    who: "Serveurs",
    icon: HandPlatter,
    hex: "#7B1E24",
    rgb: [123, 30, 36],
    steps: [
      "Connectez-vous avec votre code PIN sur la tablette POS.",
      "L'écran affiche toutes les tables et les nouvelles commandes clients en temps réel.",
      "Quand une commande arrive (son + badge doré), appuyez sur « Prendre » pour vous l'attribuer.",
      "Vérifiez la commande puis appuyez sur « Accepter » : elle part automatiquement en cuisine.",
      "Depuis l'onglet « Tables », ajoutez des articles à une table si le client commande au comptoir.",
      "Lorsque le plat est apporté au client, marquez-le « Servi ».",
    ],
    tips: [
      "Le badge doré indique le nombre de commandes actives à traiter.",
      "Un plat prêt depuis trop longtemps déclenche une relance sonore automatique.",
    ],
  },
  {
    key: "kds",
    title: "Cuisine / KDS — Préparation",
    who: "Équipe cuisine",
    icon: ChefHat,
    hex: "#B8672A",
    rgb: [184, 103, 42],
    steps: [
      "Connectez-vous avec votre PIN sur la tablette cuisine (KDS).",
      "Les commandes acceptées s'affichent automatiquement, de la plus ancienne à la plus récente.",
      "Appuyez sur « Prendre » pour signaler à l'équipe que vous préparez cette commande.",
      "Passez la commande en « En préparation », puis en « Prête » dès que le plat est terminé.",
      "Une commande « Prête » non servie déclenche une relance, puis une alerte au Manager.",
    ],
    tips: [
      "L'écran reste allumé en continu pendant le service (Wake Lock) — rien ne s'éteint.",
      "Le minuteur de chaque ticket vous aide à tenir les temps de service.",
    ],
  },
  {
    key: "pay",
    title: "Caisse / Paiement — Encaissement",
    who: "Serveurs, Caisse, Manager",
    icon: ReceiptEuro,
    hex: "#123026",
    rgb: [18, 48, 38],
    steps: [
      "Ouvrez la table concernée, puis l'onglet « Addition ».",
      "Paiement en espèces : appuyez sur « Encaisser en espèces » et confirmez le montant reçu.",
      "Paiement par carte : présentez le QR de paiement au client ou envoyez-lui le lien.",
      "Le client règle depuis son téléphone ; l'encaissement est confirmé automatiquement côté serveur.",
      "En cas d'échec de paiement, une alerte apparaît avec des actions rapides (espèces ou renvoyer le lien).",
    ],
    tips: [
      "Un paiement en ligne n'est jamais validé sans confirmation réelle côté serveur.",
      "Le client peut régler toute la table, une part, ou un montant libre.",
    ],
  },
  {
    key: "manager",
    title: "Menu / Manager — Pilotage",
    who: "Manager, Propriétaire",
    icon: BarChart3,
    hex: "#2A5A46",
    rgb: [42, 90, 70],
    steps: [
      "Accueil : vue d'ensemble en temps réel (tables, commandes, encaissements, alertes).",
      "Statistiques : chiffre d'affaires, plats populaires, satisfaction et impact NOVANEXT.",
      "Journal : historique des actions (prise, préparation, service, encaissement) avec les délais.",
      "Menu (propriétaire) : ajoutez ou modifiez les plats, catégories, prix et photos.",
      "Paramètres (propriétaire) : longueur du PIN, relances, canaux d'escalade, marque du restaurant.",
      "Fin de service : téléchargez le résumé PDF (ventes, délais moyens, relances déclenchées).",
    ],
    tips: [
      "Les alertes d'escalade peuvent être envoyées en son, en notification, ou les deux.",
      "Toutes les données affichées sont réelles et synchronisées en direct.",
    ],
  },
  {
    key: "client",
    title: "Parcours client — Commande par QR",
    who: "Clients (à titre d'information)",
    icon: QrCode,
    hex: "#C69C4A",
    rgb: [198, 156, 74],
    steps: [
      "Le client scanne le QR code posé sur sa table.",
      "Il parcourt le menu illustré et ajoute des plats à sa sélection.",
      "Il envoie sa commande : elle arrive instantanément côté serveur et en cuisine.",
      "Il suit l'avancement sur son téléphone (acceptée, en préparation, prête, servie).",
      "En fin de repas, il consulte son addition et paie (espèces via le serveur, ou carte en ligne).",
    ],
    tips: [
      "Aucune application à installer pour le client : tout se passe dans le navigateur.",
    ],
  },
  {
    key: "notif",
    title: "Notifications & fiabilité",
    who: "Tous les postes",
    icon: Bell,
    hex: "#3B5566",
    rgb: [59, 85, 102],
    steps: [
      "Chaque poste reçoit un son et une notification pour les événements importants.",
      "À la première utilisation, activez les notifications via le badge en haut de l'écran.",
      "En cas de coupure réseau, les actions sûres sont mises en file et renvoyées à la reconnexion.",
      "L'écran reste actif pendant le service pour ne manquer aucune commande.",
    ],
    tips: [
      "Un badge « en attente » indique le nombre d'actions non encore synchronisées.",
    ],
  },
];

export default function UserGuide({ restaurant }) {
  const [busy, setBusy] = useState(false);

  const buildPdf = () => {
    setBusy(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 48;
      const CW = W - M * 2;
      let y = 0;
      let page = 1;

      const footer = () => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text("NOVANEXT TABLE — Manuel d'utilisation", M, H - 24);
        doc.text(String(page), W - M, H - 24, { align: "right" });
      };
      const newPage = () => { footer(); doc.addPage(); page += 1; y = M + 8; };
      const ensure = (need) => { if (y + need > H - 48) newPage(); };

      // Cover header band
      doc.setFillColor(18, 48, 38);
      doc.rect(0, 0, W, 150, "F");
      doc.setTextColor(198, 156, 74);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("NOVANEXT TABLE", M, 58);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(23);
      doc.text("Manuel d'utilisation du staff", M, 90);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(224, 224, 214);
      if (restaurant?.name) doc.text(restaurant.name, M, 114);
      doc.setFontSize(9);
      doc.setTextColor(180, 190, 180);
      doc.text("Formation rapide — Salle, Cuisine, Caisse et Manager", M, 132);
      y = 182;

      GUIDE.forEach((s, i) => {
        const [r, g, b] = s.rgb;
        ensure(90);
        doc.setFillColor(r, g, b);
        doc.roundedRect(M, y, CW, 30, 4, 4, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.text(`${i + 1}. ${s.title}`, M + 12, y + 20);
        y += 44;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(r, g, b);
        doc.text(`POUR QUI : ${s.who.toUpperCase()}`, M, y);
        y += 18;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        doc.setTextColor(45, 45, 45);
        s.steps.forEach((st, idx) => {
          const lines = doc.splitTextToSize(`${idx + 1}.  ${st}`, CW - 10);
          ensure(lines.length * 14 + 4);
          doc.text(lines, M + 4, y);
          y += lines.length * 14 + 5;
        });

        y += 4;
        (s.tips || []).forEach((tp) => {
          const lines = doc.splitTextToSize(`Astuce :  ${tp}`, CW - 20);
          const boxH = lines.length * 13 + 14;
          ensure(boxH + 6);
          doc.setFillColor(248, 244, 233);
          doc.roundedRect(M, y - 11, CW, boxH, 3, 3, "F");
          doc.setTextColor(140, 110, 30);
          doc.setFont("helvetica", "italic");
          doc.setFontSize(9.5);
          doc.text(lines, M + 10, y);
          doc.setFont("helvetica", "normal");
          y += boxH + 4;
        });
        y += 16;
      });

      footer();
      doc.save("NOVANEXT-TABLE-Manuel-utilisation.pdf");
      toast.success("Manuel PDF téléchargé");
    } catch (e) {
      toast.error("Échec de la génération du PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="user-guide" className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="rounded-2xl bg-[#0C1713] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 border border-[#1B3028]">
        <div className="w-14 h-14 rounded-2xl bg-[#C69C4A] text-[#0C1713] grid place-items-center shrink-0">
          <LifeBuoy className="w-7 h-7" />
        </div>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-widest text-[#C69C4A] mb-1">Centre d'aide · Formation staff</p>
          <h2 className="font-serif-display text-2xl md:text-3xl leading-tight">Manuel d'utilisation NOVANEXT TABLE</h2>
          <p className="text-sm text-stone-400 mt-2 max-w-xl">
            Un guide simple, poste par poste, pour former votre équipe en quelques minutes. Téléchargez-le en un clic pour l'imprimer ou le partager.
          </p>
        </div>
        <Button
          data-testid="download-guide-pdf"
          onClick={buildPdf}
          disabled={busy}
          className="rounded-full bg-[#C69C4A] hover:bg-[#b78d3d] text-[#0C1713] font-semibold h-11 px-6 shrink-0"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Télécharger le PDF
        </Button>
      </div>

      {/* Station cards */}
      <div className="grid gap-5">
        {GUIDE.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              data-testid={`guide-section-${s.key}`}
              className="rounded-2xl bg-white border border-[#EAE5DC] overflow-hidden shadow-sm"
            >
              <div className="flex items-center gap-3 px-5 py-4" style={{ backgroundColor: s.hex }}>
                <div className="w-10 h-10 rounded-xl bg-white/15 grid place-items-center text-white shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-white font-serif-display text-lg leading-tight truncate">{i + 1}. {s.title}</h3>
                  <p className="text-white/70 text-xs mt-0.5">Pour qui : {s.who}</p>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <ol className="space-y-2.5">
                  {s.steps.map((st, idx) => (
                    <li key={idx} className="flex gap-3 text-sm text-[#2A2A2A]">
                      <span
                        className="w-5 h-5 rounded-full grid place-items-center text-[11px] font-bold text-white shrink-0 mt-0.5"
                        style={{ backgroundColor: s.hex }}
                      >
                        {idx + 1}
                      </span>
                      <span className="leading-relaxed">{st}</span>
                    </li>
                  ))}
                </ol>
                {(s.tips || []).map((tp, idx) => (
                  <div key={idx} className="flex gap-2.5 items-start rounded-xl bg-[#FBF6EA] border border-[#F0E6CE] px-3.5 py-2.5">
                    <Lightbulb className="w-4 h-4 text-[#C69C4A] shrink-0 mt-0.5" />
                    <span className="text-[13px] text-[#7A5E17] leading-relaxed">{tp}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-stone-400 pt-2 pb-6">
        <CheckCircle2 className="w-4 h-4 text-[#2A5A46]" />
        Toutes les fonctions décrites sont réelles et synchronisées en temps réel.
      </div>
    </div>
  );
}
